/**
 * ConfirmDialog — custom confirmation popup replacing window.confirm.
 * Parent conditionally renders it (no `open` prop). Overlay click / Cancel → onCancel.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useT } from '../../i18n/language-provider'
import { Button } from './button'

/** Type-to-confirm for what cannot be undone: the confirm button stays off until the input equals `value`. */
export interface ConfirmText {
  value: string
  label: string
}

interface Props {
  title: string
  message: ReactNode
  confirmLabel: string
  destructive?: boolean
  pending?: boolean
  confirmText?: ConfirmText
  /** `lg` for a message that carries a list to read before saying yes; the default stays the narrow card. */
  size?: 'sm' | 'lg'
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({ title, message, confirmLabel, destructive, pending, confirmText, size = 'sm', onConfirm, onCancel }: Props) {
  const t = useT()
  const [typed, setTyped] = useState('')
  const locked = !!confirmText && typed.trim() !== confirmText.value
  const panel = useRef<HTMLDivElement>(null)
  // Focus belongs inside while it is open (keyboard and screen readers reach OK / Cancel even when
  // another popup is underneath) and goes back to what had it when it closes.
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    if (!confirmText) document.getElementById('confirm-dialog-cancel-btn')?.focus()
    return () => { previous?.focus?.() }
  }, [confirmText])
  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') { e.stopPropagation(); if (!pending) onCancel(); return }
    if (e.key !== 'Tab' || !panel.current) return
    const items = [...panel.current.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled])')]
    if (items.length === 0) return
    const first = items[0]!
    const last = items[items.length - 1]!
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
  }
  return (
    <div id="confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="confirm-dialog-title" onKeyDown={onKeyDown} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onCancel}>
      <div ref={panel} className={`w-full ${size === 'lg' ? 'flex max-h-[90vh] max-w-3xl flex-col' : 'max-w-sm'} rounded-lg border border-border bg-surface p-6 shadow-xl`} onClick={(e) => e.stopPropagation()}>
        <h2 id="confirm-dialog-title" className="mb-2 text-base font-semibold text-fg">{title}</h2>
        {/* A wide card carries a list: the message scrolls, the title and the buttons stay in the viewport. */}
        <div tabIndex={size === 'lg' ? 0 : undefined} className={`mb-5 text-sm text-fg-soft ${size === 'lg' ? 'min-h-0 overflow-y-auto' : ''}`}>{message}</div>
        {confirmText && (
          <label className="mb-5 block text-sm text-fg-soft">
            {confirmText.label}
            <input
              id="confirm-dialog-text-input"
              autoFocus
              autoComplete="off"
              spellCheck={false}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-1.5 font-mono text-sm text-fg"
            />
          </label>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="md" id="confirm-dialog-cancel-btn"
            type="button"
            onClick={onCancel}
            disabled={pending}>
            {t('common.cancel')}
          </Button>
          <button
            id="confirm-dialog-confirm-btn"
            type="button"
            onClick={onConfirm}
            disabled={pending || locked}
            className={`rounded px-4 py-1.5 text-sm font-medium text-white disabled:opacity-50 ${destructive ? 'bg-destructive hover:opacity-90' : 'bg-primary hover:opacity-90'}`}
          >
            {pending ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
